import { ChevronRight, Loader2 } from 'lucide-react';
import { badgeVariants } from '@/components/ui/badge-variants';
import { cn } from '@/lib/utils';
import type {
  PickupComparisonView,
  PickupFieldComparison
} from '@/components/reports/pickupReconciliationPolicy';
import type { PickupDriftInspectorData } from '@/components/reports/pickupDriftInspectorService';
import {
  PICKUP_COMPARISON_VIEW_OPTIONS,
  PICKUP_LOAN_CENTER_LABEL,
  PICKUP_OPTIMAL_BLUE_LABEL,
  pickupVerificationSeverity,
  pickupVerificationSeverityClass,
  pickupVerificationShortLabel
} from '@/components/reports/pickupVerificationPresentation';
import { formatPickupComparisonValue } from '@/components/reports/pickupComparisonValueFormatter';
import { formatPickupDateTime } from './pickupPipelineFormatters';

interface PickupPipelineVerificationPanelProps {
  data: PickupDriftInspectorData | null;
  loading: boolean;
  error: string | null;
  view: PickupComparisonView;
  onViewChange: (view: PickupComparisonView) => void;
  onOpenInspector: (element: HTMLButtonElement) => void;
}

function isConflict(row: PickupFieldComparison): boolean {
  return row.verdict === 'drift' || row.verdict === 'missing_in_ob';
}

function pickupPipelineComparisonRows(
  data: PickupDriftInspectorData | null,
  view: PickupComparisonView
): PickupFieldComparison[] {
  if (!data) {
    return [];
  }
  if (view === 'blocking') {
    return data.comparisons.filter((row) => row.tier === 'blocking' && isConflict(row));
  }
  if (view === 'minor') {
    return data.comparisons.filter((row) => row.tier === 'advisory' && isConflict(row));
  }
  return data.comparisons;
}

function verdict(row: PickupFieldComparison): string {
  if (row.verdict === 'missing_in_ob') {
    return 'Not in OB';
  }
  if (row.verdict === 'drift') {
    return row.tier === 'blocking' ? 'Blocking' : 'Differs';
  }
  return row.verdict === 'normalized_match' ? 'Same format' : 'Matches';
}

export function PickupPipelineVerificationPanel({
  data,
  loading,
  error,
  view,
  onViewChange,
  onOpenInspector
}: PickupPipelineVerificationPanelProps) {
  const rows = pickupPipelineComparisonRows(data, view);
  const counts: Record<PickupComparisonView, number> = {
    all: data?.comparisons.length || 0,
    blocking: pickupPipelineComparisonRows(data, 'blocking').length,
    minor: pickupPipelineComparisonRows(data, 'minor').length
  };
  const verificationChipInput = {
    status: data?.verificationStatus,
    mismatchFields: data?.comparisons
      .filter(isConflict)
      .map(({ field, tier }) => ({ field, tier })),
    lastVerifiedAt: data?.lastVerifiedAt
  };
  const verificationChipSeverity = pickupVerificationSeverity(verificationChipInput);

  return (
    <section aria-labelledby="pickup-inline-verification" className="min-w-0">
      <div className="flex items-center justify-between gap-3">
        <h3 id="pickup-inline-verification" className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
          Lock verification — {PICKUP_LOAN_CENTER_LABEL} vs {PICKUP_OPTIMAL_BLUE_LABEL}
        </h3>
        {data && (
          <button
            type="button"
            className={cn(
              badgeVariants({ variant: 'outline' }),
              'h-6 gap-0.5 px-2 text-[10px] outline-none hover:border-primary/60 hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              pickupVerificationSeverityClass(verificationChipSeverity)
            )}
            onClick={(event) => onOpenInspector(event.currentTarget)}
            aria-label={`Open full ${PICKUP_OPTIMAL_BLUE_LABEL} comparison`}
          >
            {pickupVerificationShortLabel(verificationChipInput)}
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Verification comparison views" className="mt-2.5 flex gap-1">
        {PICKUP_COMPARISON_VIEW_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={view === value}
            className={cn(
              'rounded-md px-2.5 py-1 text-[10.5px] leading-[1.4] outline-none focus-visible:ring-2 focus-visible:ring-ring',
              view === value
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'bg-muted font-medium text-muted-foreground hover:bg-accent'
            )}
            onClick={() => onViewChange(value)}
          >
            {label} {counts[value]}
          </button>
        ))}
      </div>

      {data?.investorCommitment && (
        <p className="mt-2.5 text-[11px] font-semibold text-foreground">
          {data.investorCommitment}
        </p>
      )}

      {loading && (
        <div className="flex min-h-32 items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Reading current comparison…
        </div>
      )}
      {!loading && error && (
        <div role="alert" className="mt-2.5 border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          {error}
        </div>
      )}
      {!loading && !error && (
        <div className="mt-2.5 overflow-x-auto">
          <table className="w-full min-w-[440px] border-collapse text-[11px] leading-none tabular-nums">
            <thead className="text-[9.5px] uppercase tracking-[0.08em] text-muted-foreground">
              <tr>
                <th scope="col" className="px-1.5 pb-2 text-left font-semibold">Field</th>
                <th scope="col" className="px-1.5 pb-2 text-right font-semibold">{PICKUP_LOAN_CENTER_LABEL}</th>
                <th scope="col" className="px-1.5 pb-2 text-right font-semibold">{PICKUP_OPTIMAL_BLUE_LABEL}</th>
                <th scope="col" className="px-1.5 pb-2 text-right font-semibold">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const label = verdict(row);
                const loanCenterValue = formatPickupComparisonValue(row.field, row.encompassRaw, 'encompass');
                const optimalBlueValue = formatPickupComparisonValue(row.field, row.obRaw, 'ob');
                return (
                  <tr
                    key={row.field}
                    className={cn(
                      'border-t',
                      label === 'Blocking' && 'bg-destructive/5',
                      label === 'Differs' && 'bg-warning/5'
                    )}
                  >
                    <th scope="row" className="px-1.5 py-2 text-left font-medium">
                      <div>{row.label}</div>
                      {row.note && (
                        <div className="mt-1 text-[9.5px] font-normal leading-snug text-muted-foreground">
                          {row.note}
                        </div>
                      )}
                    </th>
                    <td className="max-w-32 px-1.5 py-2 text-right">
                      <div className="truncate whitespace-nowrap" title={loanCenterValue}>{loanCenterValue}</div>
                    </td>
                    <td className="max-w-32 px-1.5 py-2 text-right">
                      <div className="truncate whitespace-nowrap" title={optimalBlueValue}>{optimalBlueValue}</div>
                    </td>
                    <td className={cn(
                      'px-1.5 py-2 text-right font-semibold',
                      label === 'Blocking' && 'text-destructive',
                      label === 'Differs' && 'text-warning-strong',
                      label === 'Not in OB' && 'text-muted-foreground',
                      (label === 'Matches' || label === 'Same format') && 'text-muted-foreground'
                    )}>
                      {label}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="border-t px-3 py-6 text-center text-[11px] text-muted-foreground">
              No fields in this view.
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="mt-2.5 space-y-1">
          <p className={cn(
            'text-[11px] font-medium leading-relaxed',
            data.blockingCount > 0 ? 'text-destructive' : data.minorCount > 0 ? 'text-foreground' : 'text-muted-foreground'
          )}>
            {data.consequence}
          </p>
          <p className="text-[10.5px] leading-relaxed text-muted-foreground">
            {data.comparisons.length} fields compared against the cached {PICKUP_OPTIMAL_BLUE_LABEL} snapshot · last verified {formatPickupDateTime(data.lastVerifiedAt)}
          </p>
        </div>
      )}
    </section>
  );
}
